import Phaser from 'phaser';
import { ChunkManager } from '../systems/ChunkManager';
import { PlayerController } from '../systems/PlayerController';
import { CHUNK_SIZE, TILE_WIDTH, TILE_HEIGHT } from '../constants';
import { cartToIso } from '../utils/IsoUtils';

/**
 * GameScene
 * ─────────
 * The main gameplay scene.  Owns the procedural world (via ChunkManager)
 * and the player (via PlayerController).  Camera smoothly follows the
 * player.  Launches the UIScene overlay on top.
 */
export class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
  }

  create() {
    // Systems
    this.chunkManager = new ChunkManager(this);
    this.player = new PlayerController(this, 0, 0);

    // Initial chunk load around spawn
    this.chunkManager.update(this.player.cartX, this.player.cartY);

    // Camera follows the player sprite
    this.cameras.main.startFollow(this.player.sprite, true, 1, 1);
    this.cameras.main.setZoom(4);

    // Launch the HUD overlay scene in parallel
    this.scene.launch('UIScene');

    // Inventory toggle – 'I' key
    this.inventoryKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.I);
    this.inventoryOpen = false;

    // Debug overlay – 'B' key
    this.debugKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.B);
    this.debugGfx = this.add.graphics();
    this.debugGfx.setDepth(999999);
    this.showDebug = false;

    // Track which chunk the player was in so we only recalculate when they move to a new one
    this.lastChunkX = null;
    this.lastChunkY = null;
  }

  update(_time, delta) {
    // Player movement
    this.player.update(delta);

    // Chunk loading – only recalculate when chunk boundary crossed
    const cx = Math.floor(this.player.cartX / CHUNK_SIZE);
    const cy = Math.floor(this.player.cartY / CHUNK_SIZE);
    if (cx !== this.lastChunkX || cy !== this.lastChunkY) {
      this.chunkManager.update(this.player.cartX, this.player.cartY);
      this.lastChunkX = cx;
      this.lastChunkY = cy;
    }

    // Inventory toggle
    if (Phaser.Input.Keyboard.JustDown(this.inventoryKey)) {
      if (this.inventoryOpen) {
        this.scene.stop('InventoryScene');
        this.inventoryOpen = false;
      } else {
        this.scene.launch('InventoryScene');
        this.inventoryOpen = true;
      }
    }

    // Debug overlay toggle
    if (Phaser.Input.Keyboard.JustDown(this.debugKey)) {
      this.showDebug = !this.showDebug;
      this.registry.set('showDebug', this.showDebug);
    }
    this.drawDebug();
  }

  /* ── Debug visualisation ─────────────────────────────────────── */

  drawDebug() {
    this.debugGfx.clear();
    if (!this.showDebug) return;

    const hw = TILE_WIDTH / 2;
    const hh = TILE_HEIGHT / 2;

    // Helper: draw an isometric diamond for a tile
    const drawDiamond = (tileX, tileY, color, alpha) => {
      const { x, y } = cartToIso(tileX + 0.5, tileY + 0.5);
      this.debugGfx.fillStyle(color, alpha);
      this.debugGfx.fillPoints([
        new Phaser.Geom.Point(x, y - hh),
        new Phaser.Geom.Point(x + hw, y),
        new Phaser.Geom.Point(x, y + hh),
        new Phaser.Geom.Point(x - hw, y),
      ], true);
      this.debugGfx.lineStyle(1, color, alpha + 0.2);
      this.debugGfx.strokePoints([
        new Phaser.Geom.Point(x, y - hh),
        new Phaser.Geom.Point(x + hw, y),
        new Phaser.Geom.Point(x, y + hh),
        new Phaser.Geom.Point(x - hw, y),
      ], true);
    };

    // Draw all blocked tiles in red (within a reasonable radius)
    const px = this.player.cartX;
    const py = this.player.cartY;
    const viewRange = 12;
    const blocked = this.chunkManager.blocked;

    for (let dx = -viewRange; dx <= viewRange; dx++) {
      for (let dy = -viewRange; dy <= viewRange; dy++) {
        const tileX = Math.floor(px) + dx;
        const tileY = Math.floor(py) + dy;
        if (blocked.has(`${tileX},${tileY}`)) {
          drawDiamond(tileX, tileY, 0xff0000, 0.3);
        }
      }
    }

    // Draw player collision radius in yellow at foot position
    const FOOT = 0.5;
    const fpx = px - FOOT;
    const fpy = py - FOOT;
    const r = 0.25;
    const corners = [
      [fpx - r, fpy - r],
      [fpx + r, fpy - r],
      [fpx + r, fpy + r],
      [fpx - r, fpy + r],
    ];
    const screenCorners = corners.map(([cx, cy]) => {
      const { x, y } = cartToIso(cx, cy);
      return new Phaser.Geom.Point(x, y);
    });
    this.debugGfx.fillStyle(0xffff00, 0.3);
    this.debugGfx.fillPoints(screenCorners, true);
    this.debugGfx.lineStyle(1, 0xffff00, 0.8);
    this.debugGfx.strokePoints(screenCorners, true);

    // Draw foot dot
    const { x: pcx, y: pcy } = cartToIso(fpx, fpy);
    this.debugGfx.fillStyle(0x00ff00, 1);
    this.debugGfx.fillCircle(pcx, pcy, 2);
  }
}
